import React, { useState } from 'react';

/**
 * DateRangePicker — the single canonical range picker for the app.
 * Quick presets (היום / השבוע / סופ"ש / מותאם) + prev/next stepping that is
 * never clamped, so future dates (presales) are always reachable.
 *
 * Props: { start, end, onChange, label, mode }
 * - onChange receives { start, end, mode }. Pages that only care about the
 *   range can ignore `mode`.
 * - `mode` prop is optional; when provided the picker is controlled
 *   (ChartsPage), otherwise it manages mode internally.
 */

function toDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fromDateStr(s) {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function israelToday() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Jerusalem' });
}

// Day of the week in Israel: Sun=0 … Sat=6.
export function israelDow() {
  return fromDateStr(israelToday()).getDay();
}

// '2026-11-01' → '1.11'
export function fmtShortDate(s) {
  if (!s) return '';
  const [, m, d] = s.split('-').map(Number);
  return `${d}.${m}`;
}

// A range in the short form: '5-8.11' inside one month, '30.10-2.11' across two.
export function fmtShortRange(start, end) {
  if (!start || !end) return '';
  if (start === end) return fmtShortDate(start);
  const [ys, ms, ds] = start.split('-').map(Number);
  const [ye, me, de] = end.split('-').map(Number);
  if (ys === ye && ms === me) return `${ds}-${de}.${me}`;
  return `${fmtShortDate(start)}-${fmtShortDate(end)}`;
}

export function shiftStr(s, days) {
  const d = fromDateStr(s);
  d.setDate(d.getDate() + days);
  return toDateStr(d);
}

export function currentIsraeliWeek() {
  const today = fromDateStr(israelToday());
  const sunday = new Date(today);
  sunday.setDate(today.getDate() - today.getDay());
  const saturday = new Date(sunday);
  saturday.setDate(sunday.getDate() + 6);
  return { start: toDateStr(sunday), end: toDateStr(saturday) };
}

// Thu–Sun is the Israeli cinema weekend. Exported so callers can label a
// range as the one happening *now* rather than the last completed one —
// relevantWeekend() already switches on the same condition.
export function isInWeekend() {
  const dow = fromDateStr(israelToday()).getDay(); // Sun=0 … Sat=6
  return dow === 0 || dow >= 4;
}

export function relevantWeekend() {
  const today = fromDateStr(israelToday());
  const dow = today.getDay(); // Sun=0 … Sat=6
  if (isInWeekend()) {
    // Inside a weekend (Thu=4, Fri=5, Sat=6, Sun=0) → return current Thu–Sun
    const daysFromThu = dow === 0 ? 3 : dow - 4;
    const thisThu = new Date(today);
    thisThu.setDate(today.getDate() - daysFromThu);
    const thisSun = new Date(thisThu);
    thisSun.setDate(thisThu.getDate() + 3);
    return { start: toDateStr(thisThu), end: toDateStr(thisSun) };
  }
  // Mon–Wed → last completed weekend
  const lastSun = new Date(today);
  lastSun.setDate(today.getDate() - dow);
  const lastThu = new Date(lastSun);
  lastThu.setDate(lastSun.getDate() - 3);
  return { start: toDateStr(lastThu), end: toDateStr(lastSun) };
}

function spanDays(start, end) {
  return Math.max(1, Math.round((fromDateStr(end) - fromDateStr(start)) / 86400000) + 1);
}

function detectMode(start, end) {
  if (!start || !end) return 'custom';
  if (start === end) return 'today';
  const span = spanDays(start, end);
  const startDow = fromDateStr(start).getDay();
  if (span === 7 && startDow === 0) return 'week';
  if (span === 4 && startDow === 4) return 'weekend';
  return 'custom';
}

const HEB_DAYS = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'];
const fmtDMY = (s) => { const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };
const fmtDM = (s) => { const [, m, d] = s.split('-'); return `${d}/${m}`; };

function rangeLabel(start, end) {
  if (!start || !end) return '—';
  if (start === end) return `יום ${HEB_DAYS[fromDateStr(start).getDay()]} · ${fmtDMY(start)}`;
  return `${fmtDM(start)} – ${fmtDMY(end)}`;
}

const MODES = [
  { key: 'today', label: 'היום' },
  { key: 'week', label: 'השבוע' },
  { key: 'weekend', label: 'סופ"ש' },
  { key: 'custom', label: 'מותאם' },
];

function DateRangePicker({ start, end, onChange, label, mode: modeProp }) {
  const [modeState, setModeState] = useState(() => detectMode(start, end));
  const mode = modeProp || modeState;
  const setMode = (m) => setModeState(m);

  const today = israelToday();
  const fire = (range, m) => { setMode(m); onChange({ ...range, mode: m }); };

  const selectMode = (m) => {
    if (m === 'today') fire({ start: today, end: today }, 'today');
    else if (m === 'week') fire(currentIsraeliWeek(), 'week');
    else if (m === 'weekend') fire(relevantWeekend(), 'weekend');
    else if (modeProp) fire({ start, end }, 'custom'); // controlled parent owns mode
    // Uncontrolled custom: pure UI change — the range is unchanged, and firing
    // onChange would make pages refetch and unmount the picker, resetting mode
    // before the user can touch the date inputs.
    else setModeState('custom');
  };

  const step = (dir) => { // dir: +1 → forward (future), -1 → back (past)
    const days = mode === 'today' ? 1
      : (mode === 'week' || mode === 'weekend') ? 7
        : spanDays(start, end);
    fire({ start: shiftStr(start, dir * days), end: shiftStr(end, dir * days) }, mode);
  };

  const setManual = (key, val) => {
    if (!val) return;
    let s = key === 'start' ? val : start;
    let e = key === 'end' ? val : end;
    if (s > e) [s, e] = key === 'start' ? [s, s] : [e, e]; // normalize instead of blocking
    fire({ start: s, end: e }, 'custom');
  };

  const isFuture = start > today;
  const isDark = document.documentElement.dataset.theme === 'dark';

  const chip = (m) => {
    const on = mode === m.key;
    return (
      <button key={m.key} onClick={() => selectMode(m.key)} style={{
        padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: on ? 700 : 400,
        fontFamily: 'Heebo, sans-serif', cursor: 'pointer',
        border: `1px solid ${on ? 'var(--accent)' : 'var(--brd)'}`,
        background: on ? 'var(--accent)' : 'var(--surf-alt)', color: on ? '#fff' : 'var(--muted)',
        transition: 'all .15s', whiteSpace: 'nowrap',
      }}>{m.label}</button>
    );
  };

  const navBtn = (txt, fn, title) => (
    <button onClick={fn} title={title} style={{
      padding: '6px 12px', borderRadius: 8, border: '1px solid var(--brd)',
      background: 'var(--surf-alt)', color: 'var(--txt)', fontFamily: 'Heebo, sans-serif',
      fontSize: 12, whiteSpace: 'nowrap', cursor: 'pointer', transition: 'background .12s',
    }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--surf-hov)'}
      onMouseLeave={e => e.currentTarget.style.background = 'var(--surf-alt)'}
    >{txt}</button>
  );

  const inputStyle = {
    background: 'var(--surf-alt)', border: '1px solid var(--brd)', borderRadius: 6,
    padding: '4px 7px', color: 'var(--txt)', fontSize: 12, fontFamily: 'Heebo, sans-serif',
    outline: 'none', colorScheme: isDark ? 'dark' : 'light',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
      {label && <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--txt)' }}>{label}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6 }}>{MODES.map(chip)}</div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, direction: 'ltr' }}>
          {navBtn('‹ קודם', () => step(-1), 'אחורה בזמן')}
          <div style={{
            minWidth: 140, textAlign: 'center', fontSize: 13, fontWeight: 600, color: 'var(--txt)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, direction: 'rtl',
          }}>
            <span>{rangeLabel(start, end)}</span>
            {isFuture && (
              <span style={{
                fontSize: 10, fontWeight: 700, color: '#7c3aed', background: 'rgba(124,58,237,0.12)',
                padding: '2px 7px', borderRadius: 6, whiteSpace: 'nowrap',
              }}>מכירה מוקדמת</span>
            )}
          </div>
          {navBtn('הבא ›', () => step(1), 'קדימה בזמן')}
        </div>

        {mode === 'custom' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, direction: 'ltr' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 11, color: 'var(--muted)' }}>עד:</span>
              <input type="date" value={end} onChange={e => setManual('end', e.target.value)} style={inputStyle} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 11, color: 'var(--muted)' }}>מ:</span>
              <input type="date" value={start} onChange={e => setManual('start', e.target.value)} style={inputStyle} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DateRangePicker;
